import { Capacitor } from "@capacitor/core";
import { FirebaseApp, getApp, getApps, initializeApp } from "firebase/app";
import { ReCaptchaV3Provider, initializeAppCheck } from "firebase/app-check";
import { connectAuthEmulator, getAuth, indexedDBLocalPersistence, initializeAuth } from "firebase/auth";
import { Firestore, collection, connectFirestoreEmulator, deleteDoc, doc, getDoc, getDocs, getFirestore, initializeFirestore, persistentLocalCache, persistentSingleTabManager, limit, onSnapshot, orderBy, query, runTransaction, setDoc, updateDoc, where } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";
import { UploadMetadata, UploadTask, connectStorageEmulator, getDownloadURL, getStorage, ref, uploadBytesResumable, deleteObject, TaskState } from "firebase/storage";
import { firebaseConfig } from "../const/Settings";
import { AIBot } from "../models/AIBot";
import { DocSummary, TrainDocuBotResponse } from "../models/DTO";
import { DocuBot } from "../models/DocuBot";
import { Message } from "../models/Message";
import { Notification } from "../models/Notification";
import { Room } from "../models/Room";
import { RoomInvite } from "../models/RoomInvite";
import { User, UserSimplified } from "../models/User";
import { UserBlock } from "../models/UserBlock";
import { UserReport } from "../models/UserReport";
import { FireClientAuth } from "./FireClientAuth";
import { FireClientBot } from "./FireClientBot";
import { FireClientCall } from "./FireClientCall";
import { FireClientItem } from "./FireClientItem";
import { FireClientMsg } from "./FireClientMsg";
import { FireClientPayment } from "./FireClientPayment";
import { FireClientWorkspace } from "./FireClientWorkspace";
import { My } from "./My";
import { MyNotifs } from "./MyNotifs";
import { Util, messagePojo } from "./Utils/Util";

interface UploadFileOptions {
  onTask?: (task: UploadTask) => void;
  onComplete?: (url: string | null) => void;
  onProgress?: (percent: number) => void;
  onStateChange?: (state: TaskState) => void;
}

const env = import.meta.env;

// setLogLevel('debug');
const FB_REGION = 'asia-northeast1';

if (getApps().length === 0) {
  const app = initializeApp(firebaseConfig);
  console.log("Firebase initialized");

  // Firestore 초기화 - 캐시 크기 제한으로 iOS Safari IndexedDB 메모리 문제 완화
  // 기본값은 무제한이지만, 50MB로 제한하여 메모리 사용량 관리
  initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentSingleTabManager({ forceOwnership: false }),
      cacheSizeBytes: 50 * 1024 * 1024 // 50MB (기본값: 무제한)
    })
  });
  console.log("Firestore initialized with 50MB cache limit");

  // 네이티브 플랫폼에서는 Auth를 즉시 초기화하여 웹용 GAPI 자동 로드 방지
  if (Capacitor.isNativePlatform()) {
    initializeAuth(app, {
      persistence: indexedDBLocalPersistence
    });
    console.log("Firebase Auth initialized for native platform");
  }
}

configureFirebase();

export function getFunc(funcName: string) {
  const funcs = getFunctions(getApp(), FB_REGION);
  const func = httpsCallable(funcs, funcName, { timeout: 535 * 1000 });
  return func;
}

export class FireClient {
  app: FirebaseApp;
  store: Firestore;
  auth: FireClientAuth;
  call: FireClientCall;
  item: FireClientItem;
  ws: FireClientWorkspace;
  payment: FireClientPayment;
  msg: FireClientMsg;
  bot: FireClientBot;

  constructor() {
    this.app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
    this.store = getFirestore(this.app);
    // Auth는 모듈 상단에서 이미 초기화됨 (네이티브) 또는 getAuth()로 자동 초기화됨 (웹)

    this.auth = new FireClientAuth(this);
    this.call = new FireClientCall(this);
    this.item = new FireClientItem(this);
    this.ws = new FireClientWorkspace(this);
    this.payment = new FireClientPayment(this);
    this.msg = new FireClientMsg(this);
    this.bot = new FireClientBot(this);
  }

  async saveRoom(room: Room) {
    await setDoc(doc(this.store, Room.COL, room.id), Util.pojo(room));
  }

  async updateField(col: string, id: string, value: any) {
    await updateDoc(doc(this.store, col, id), value);
  }

  subscribeToMyRooms(userId: string, handler: (rooms: Room[]) => void) {
    const q = query(collection(this.store, Room.COL), where("userIds", "array-contains", userId));
    const unsubscribe = onSnapshot(q, (snap) => {
      handler(snap.docs.map(d => d.data() as Room));
    });
    return unsubscribe;
  }

  async deleteRoom(roomId: string) {
    await deleteDoc(doc(this.store, Room.COL, roomId));
  }

  async getRoom(roomId: string) {
    let d = doc(this.store, Room.COL, roomId);
    return (await getDoc(d)).data() as Room;
  }

  async updateRoomDetails(room: Room) {
    await runTransaction(this.store, async (t) => {
      const roomDoc = doc(this.store, Room.COL, room.id);
      let existingRoom = (await t.get(roomDoc)).data() as Room;
      existingRoom.name = room.name;
      existingRoom.description = room.description;
      existingRoom.workspaceId = room.workspaceId;
      existingRoom.visibility = room.visibility;
      existingRoom.profileImageUrl = room.profileImageUrl;
      t.update(roomDoc, Util.pojo(existingRoom));
    });
  }

  async updateRoomBots(roomId: string, bots: AIBot[]) {
    await runTransaction(this.store, async (t) => {
      const roomDoc = doc(this.store, Room.COL, roomId);
      t.update(roomDoc, { bots: Util.pojo(bots) });
    });
  }

  async addRoomMember(roomId: string, member: User, admin: User) {
    await runTransaction(this.store, async (t) => {
      const roomDoc = doc(this.store, Room.COL, roomId);
      let room = (await t.get(roomDoc)).data() as Room;
      if (!room.userIds.find(id => id === member.id)) {
        Room.addUser(room, member);
        let msg = new Message(room, admin);
        msg.autoMessage = 'added';
        msg.autoMessageUser = member;
        t.set(doc(this.store, Message.COL, msg.id), messagePojo(msg));
        t.update(roomDoc, Util.pojo(room));
      }
    });
  }

  async removeRoomMember(roomId: string, member: User, admin: User) {
    await runTransaction(this.store, async (t) => {
      const roomDoc = doc(this.store, Room.COL, roomId);
      let room = (await t.get(roomDoc)).data() as Room;

      if (room.userIds.find(id => id === member.id)) {
        Room.removeUser(room, member.id);
        let msg = new Message(room, admin);
        msg.autoMessage = 'removed';
        msg.autoMessageUser = member;
        t.set(doc(this.store, Message.COL, msg.id), messagePojo(msg));
        t.update(roomDoc, Util.pojo(room));
      }
    });
  }

  // depricated
  async updateRoomMembers(roomId: string, roomMembers: User[], user: User) {
    await runTransaction(this.store, async (t) => {
      const roomDoc = doc(this.store, Room.COL, roomId);
      let existingRoom = (await t.get(roomDoc)).data() as Room;

      roomMembers.forEach(usr => {
        if (!existingRoom.users.find(u => u.id === usr.id)) {
          Room.addUser(existingRoom, usr);
          let msg = new Message(existingRoom, user);
          msg.autoMessage = 'added';
          msg.autoMessageUser = usr;
          t.set(doc(this.store, Message.COL, msg.id), messagePojo(msg));
        }
      })

      existingRoom.users.forEach(usr => {
        if (!roomMembers.find(u => u.id === usr.id)) {
          Room.removeUser(existingRoom, usr.id);
          let msg = new Message(existingRoom, user);
          msg.autoMessage = 'removed';
          msg.autoMessageUser = usr;
          t.set(doc(this.store, Message.COL, msg.id), messagePojo(msg));
        }
      })
      t.update(roomDoc, Util.pojo(existingRoom));
    });
  }

  async joinRoom(roomId: string, user: User, inviter: User | undefined) {
    // TODO Change this to a function api request.
    await runTransaction(this.store, async (t) => {
      const roomDoc = doc(this.store, Room.COL, roomId);
      let room = (await t.get(roomDoc)).data() as Room;
      Room.addUser(room, user);
      t.update(roomDoc, Util.pojo(room));

      let msg = new Message(room, user);
      msg.autoMessage = 'joined';
      msg.autoMessageUser = inviter;
      t.set(doc(this.store, Message.COL, msg.id), messagePojo(msg));
    });
  }

  async leaveRoom(roomId: string, user: User) {
    await runTransaction(this.store, async (t) => {
      const roomDoc = doc(this.store, Room.COL, roomId);
      let room = (await t.get(roomDoc)).data() as Room;
      Room.removeUser(room, user.id);
      t.update(roomDoc, Util.pojo(room));

      let msg = new Message(room, user);
      msg.autoMessage = 'left';
      t.set(doc(this.store, Message.COL, msg.id), messagePojo(msg));
    });
  }

  async getRooms(): Promise<Room[]> {
    const docRef = collection(this.store, "rooms");
    const snap = await getDocs(docRef);
    return snap.docs.map(d => d.data() as Room)
  }

  async getPublicRooms(): Promise<Room[]> {
    const docRef = query(collection(this.store, "rooms"), where('isPublic', '==', true));
    const snap = await getDocs(docRef);
    return snap.docs.map(d => d.data() as Room)
  }


  async saveNotifications(notifs: Notification[]) {
    try {
      await runTransaction(this.store, async (transaction) => {
        notifs.forEach(notif => {
          transaction.set(doc(this.store, Notification.COL, notif.id), Util.pojo(notif));
        })
      });
      // console.log("Transaction successfully committed!");
    } catch (e) {
      console.error("Transaction failed: ", e);
    }
  }

  async getUsers(): Promise<User[]> {
    const q = query(collection(this.store, User.COL));
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as User)
  }

  async getUser(uid: string): Promise<User | undefined> {
    let d = doc(this.store, User.COL, uid);
    return (await getDoc(d)).data() as User;
  }

  async saveUser(user: User) {
    await setDoc(doc(this.store, User.COL, user.id), Util.pojo(user));
  }

  async saveCurrentUser(user: User) {
    await setDoc(doc(this.store, User.COL, user.id), Util.pojo(My.user));
  }

  async saveNotification(notif: Notification) {
    await setDoc(doc(this.store, Notification.COL, notif.id), Util.pojo(notif));
  }

  async clearNotificationByMessageId(messageId: string) {
    let notif = MyNotifs.NotificationsByMessage.get(messageId);
    if (notif && notif.isNew) {
      await updateDoc(doc(this.store, Notification.COL, notif.id), { isNew: false });
    }
  }

  async clearNotifications(notifs: Notification[]) {
    if (notifs.length > 0) {
      await runTransaction(this.store, async (transaction) => {
        notifs.forEach(n => {
          if (n.isNew)
            transaction.update(doc(this.store, Notification.COL, n.id), { isNew: false });
        })
      });
    }
  }

  subscribeToNewNotifications(userId: string, handler: (notifs: Notification[]) => void) {
    const q = query(collection(this.store, Notification.COL), where("toUserId", "==", userId), orderBy('created', 'desc'), limit(50));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const notifs: Notification[] = [];
      querySnapshot.forEach((doc) => { notifs.push(doc.data() as Notification); });
      handler(notifs);
    });
    return unsubscribe;
  }

  uploadFile(blob: Blob, path: string, onDone: (url: string | null) => void, onProgress: null | ((percent: number) => void)) {
    const storage = getStorage();
    const metadata = this.getRightMetadata(path);
    const uploadTask = uploadBytesResumable(ref(storage, path.normalize('NFC')), blob, metadata);

    // Listen for state changes, errors, and completion of the upload.
    uploadTask.on('state_changed',
      (snapshot) => {
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        const progress = Math.round(snapshot.bytesTransferred * 100 / snapshot.totalBytes);
        // console.log('Upload is ' + progress + '% done');
        if (onProgress)
          onProgress(progress);

        switch (snapshot.state) {
          case 'paused':
            console.log('Upload is paused');
            break;
          case 'running':
            break;
        }
      },
      (error) => {

        console.error("Upload failed", error);
        onDone(null);

        // A full list of error codes is available at
        // https://firebase.google.com/docs/storage/web/handle-errors
        switch (error.code) {
          case 'storage/unauthorized':
            // User doesn't have permission to access the object
            break;
          case 'storage/canceled':
            // User canceled the upload
            break;
          case 'storage/unknown':
            // Unknown error occurred, inspect error.serverResponse
            break;
        }
      },
      () => {
        // Upload completed successfully, now we can get the download URL
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          console.log('Uploaded file at', downloadURL);
          onDone(downloadURL);
        });
      }
    );
  }

  uploadFileOptions(blob: Blob, path: string, options: UploadFileOptions = {}) {
    const {
      onTask = () => { },
      onComplete = () => { },
      onProgress,
      onStateChange = () => { }
    } = options;

    const storage = getStorage();
    const metadata = this.getRightMetadata(path);
    const uploadTask = uploadBytesResumable(ref(storage, path.normalize('NFC')), blob, metadata);

    onTask(uploadTask);

    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = Math.round(snapshot.bytesTransferred * 100 / snapshot.totalBytes);

        if (onProgress) {
          onProgress(progress);
        }

        switch (snapshot.state) {
          case 'paused':
            onStateChange('paused');
            console.log('Upload is paused');
            break;
          case 'running':
            break;
        }
      },
      (error) => {
        console.error("Upload failed", error);

        switch (error.code) {
          case 'storage/unauthorized':
            onComplete(null);
            break;
          case 'storage/canceled':
            onStateChange('canceled');
            break;
          case 'storage/unknown':
            onComplete(null);
            break;
        }
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          onComplete(downloadURL);
        });
      }
    );
  }

  async deleteFile(filePath: string) {
    const storage = getStorage();
    const fileRef = ref(storage, filePath);
    try {
      await deleteObject(fileRef);
    } catch (error) {
      console.error("파일 삭제 실패:", error);
    }
  }

  getRightMetadata(filepath: string): UploadMetadata | undefined {
    if (filepath.endsWith('.txt')) {
      return { contentType: 'text/plain; charset=utf-8' };
    } else {
      return undefined;
    }
  }
  async getCurrentInvite(room: Room, user: User): Promise<RoomInvite> {
    const q = query(collection(this.store, RoomInvite.COL), where("roomId", "==", room.id), where("creator.id", "==", user.id));
    const snap = await getDocs(q);
    console.log("Room invites found:", snap.docs.length);
    if (snap.docs.length > 0) {
      const invite = snap.docs[0].data() as RoomInvite;
      return invite;
    } else {
      user.roomSettings = [];
      user.notificationSettings = [];
      let newInvite = new RoomInvite(room.id, user);
      newInvite.roomName = room.name;
      newInvite.roomImageUrl = room.profileImageUrl;
      newInvite.roomDescription = room.description;
      newInvite.roomUserImages = room.users.map(u => u.photoUrl ?? Util.ANONYMOUS_PHOTO_URL);
      await Fire.saveRoomInvite(newInvite)
      return newInvite;
    }
  }
  async getRoomInvite(inviteId: string) {
    let d = doc(this.store, RoomInvite.COL, inviteId);
    return (await getDoc(d)).data() as RoomInvite;
  }
  async saveRoomInvite(invite: RoomInvite) {
    // creator 필드 최소화
    const minimalInvite = {
      ...Util.pojo(invite),
      creator: {
        id: invite.creator.id,
        name: invite.creator.name,
        photoUrl: invite.creator.photoUrl ?? null,
      }
    };
    await setDoc(doc(this.store, RoomInvite.COL, invite.id), minimalInvite);
  }
  async deleteRoomInvite(inviteId: string) {
    await deleteDoc(doc(this.store, RoomInvite.COL, inviteId));
  }

  async reportContent(msg: Message) {
    let rep = new UserReport(msg, My.user);
    await setDoc(doc(this.store, UserReport.COL, rep.id), Util.pojo(rep));
  }

  async saveUserBlock(block: UserBlock) {
    await setDoc(doc(this.store, User.COL, block.byUserId, UserBlock.COL, block.id), Util.pojo(block));
  }

  async deleteUserBlock(userId: string, userBlockId: string) {
    await deleteDoc(doc(this.store, User.COL, userId, UserBlock.COL, userBlockId));
  }

  async getUserBlocks(userId: string): Promise<UserBlock[]> {
    const refs = collection(this.store, User.COL, userId, UserBlock.COL);
    const snap = await getDocs(refs);
    return snap.docs.map(d => d.data() as UserBlock)
  }

  async getFlaggedMessages(): Promise<UserReport[]> {
    const refs = collection(this.store, UserReport.COL);
    const snaps = await getDocs(refs);
    return snaps.docs.map(d => d.data() as UserReport);
  }

  async deleteUserReport(report: UserReport) {
    await deleteDoc(doc(this.store, UserReport.COL, report.id));
  }

  async searchForUsers(value: string): Promise<UserSimplified[]> {
    let func = getFunc('searchForUsers');
    let res = await func({ searchKey: value });
    return res.data as UserSimplified[];
  }

  async getServerTime(): Promise<string> {
    let func = getFunc('getServerTime');
    let res = await func({});
    return res.data as string;
  }

  async getMp4Base64(webm64: string): Promise<string> {
    let func = getFunc('ffmpegTest');
    let res = await func({ webm64: webm64 });
    return res.data as string;
  }

  async updateUserRoomNotificationSetting(user: User) {
    await updateDoc(doc(this.store, User.COL, user.id), { roomSettings: Util.pojo(user.roomSettings) });
  }

  async getSearchSecuredAPIKey(roomId?: string) {
    let func = getFunc('getSearchSecuredAPIKey');
    let res = await func(roomId ? { roomId } : {});
    return res.data as string;
  }

  async deleteDocuBot(bot: DocuBot) {
    let func = getFunc('deleteDocubot');
    let res = await func({ bot: bot });
    return res.data as string;
  }

  async askDocuBot(bot: DocuBot, prompt: string): Promise<string> {
    let func = getFunc('AskDocuBot');
    let res = await func({ bot: Util.pojo(bot), prompt: prompt });
    return res.data as string;
  }

  async getBotSummaries(bot: DocuBot): Promise<DocSummary[]> {
    let d = await getDoc(doc(this.store, Room.COL, bot.roomId, "summaries", bot.id))
    const sum = d.data() as { summaries: DocSummary[] };
    return sum?.summaries ?? [];
  }

  async createSummaries(bot: DocuBot) {
    let func = getFunc('CreateBotSummaries');
    let res = await func({ bot_id: bot.id, room_id: bot.roomId });
    return res.data as DocSummary[];
  }

  async trainDocuBot(b: DocuBot): Promise<TrainDocuBotResponse> {
    let func = getFunc('TrainDocuBot');
    let res = await func({ bot: Util.pojo(b) });
    return res.data as TrainDocuBotResponse;
  }

  // async makeSureUserHasAPersonalRoom(user: User, roomList: Room[]) {
  //   if (!roomList.find(r => r.visibility === 'personal')) {
  //     let room = new Room(user.name, user.id);
  //     room.visibility = 'personal';
  //     Room.addUser(room, user);
  //     await this.saveRoom(room);
  //   }
  // }
}

export const Fire = new FireClient();

function configureFirebase() {
  const app = getApp();
  const functions = getFunctions(app, FB_REGION);

  if (!Capacitor.isNativePlatform() && typeof self !== 'undefined') {
    // 로컬 개발 환경에서는 디버그 토큰 사용
    if (Util.isLocalDevelopment()) {
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = env.VITE_FIREBASE_DEBUG_TOKEN || true;
    }
    initializeAppCheck(app, { provider: new ReCaptchaV3Provider(env.VITE_RECAPTCHA_SITE_KEY), isTokenAutoRefreshEnabled: true });
  }
  if (Util.isLocalDevelopment()) {
    if (env.VITE_USE_EMULATOR_AUTH) {
      console.log("Using Auth Emulator");
      connectAuthEmulator(getAuth(app), "http://localhost:9099");
    }
    if (env.VITE_USE_EMULATOR_FUNCTIONS) {
      console.log("Using Functions Emulator");
      connectFunctionsEmulator(functions, "localhost", 5001);
    }
    if (env.VITE_USE_EMULATOR_FIRESTORE) {
      console.log("Using Firestore Emulator");
      connectFirestoreEmulator(getFirestore(app), 'localhost', 8080);
    }
    if (env.VITE_USE_EMULATOR_STORAGE) {
      console.log("Using Storage Emulator");
      connectStorageEmulator(getStorage(app), 'localhost', 9199);
    }
  }
}